// React
import { useState, useRef, useEffect } from 'react'

// Styles
import './ColumnsAndCards.css'

// Components
import Column from './Column'
import CardModal from './CardModal'
import ColumnModal from './ColumnModal'

// Hooks
import useColumns from '../hooks/useColumns'

// Services
import { cardService } from '../services/cardService'
import { columnService } from '../services/columnService'

// ─── MOCK FLAG ───────────────────────────────────────────────────────────────
const USE_MOCK = false;

const COLUMNS_MOCK = {
    1: [
        { numColumn: 1, name: 'Backlog', cards: [
            { numCard: 1, title: 'Criar wireframe', description: 'Criar o wireframe para a nova página de login' },
            { numCard: 2, title: 'Definir paleta de cores', description: 'Escolher as cores para o novo design do site' },
        ]},
        { numColumn: 2, name: 'Em Desenvolvimento', cards: [
            { numCard: 3, title: 'Desenvolver API', description: 'Criar a API para o novo sistema de autenticação' },
        ]},
        { numColumn: 3, name: 'Em Revisão', cards: [
            { numCard: 4, title: 'Code review da tela de login', description: 'Revisar o código antes de subir para produção' },
        ]},
        { numColumn: 4, name: 'Publicado', cards: [
            { numCard: 5, title: 'Configurar ambiente', description: 'Instalar todas as dependências e configurar o ambiente' },
        ]},
    ],
};
// ─────────────────────────────────────────────────────────────────────────────

const ColumnsAndCards = ({ boardId }) => {

    const [cardModalOpen, setCardModalOpen] = useState(false)
    const [columnModalOpen, setColumnModalOpen] = useState(false)
    const [editingCard, setEditingCard] = useState(null)
    const [editingColumn, setEditingColumn] = useState(null)
    const [targetColumnId, setTargetColumnId] = useState(null)
    const [localCards, setLocalCards] = useState({})
    const [cardApiError, setCardApiError] = useState(null)

    const { columns, isLoading, error, loadColumns } = useColumns(false, boardId)

    const dragRef = useRef({ cardId: null, fromColumnId: null })

    useEffect(() => {
        console.log('Passou aqui no useEffect de ColumnsAndCards, boardId:', boardId)
        loadColumns(boardId);
    }, [boardId])

    // ── Carrega os cards quando as colunas chegarem da API ────────────────────
    useEffect(() => {
        if (USE_MOCK || !columns || columns.length === 0) return

        const fetchAllCards = async () => {
            const entries = await Promise.all(
                columns.map(async (col) => {
                    try {
                        const cards = await cardService.getCardsByColumn(col.numColumn)
                        return [col.numColumn, cards]
                    } catch (err) {
                        console.error(`Erro ao carregar cards da coluna ${col.numColumn}:`, err)
                        return [col.numColumn, []]
                    }
                })
            )
            setLocalCards(Object.fromEntries(entries))
        }

        fetchAllCards()
    }, [columns])
    // ─────────────────────────────────────────────────────────────────────────


    // ── Helpers ───────────────────────────────────────────────────────────────
    const getCards = (numColumn) => localCards[numColumn] ?? []

    // ── Card handlers ────────────────────────────────────────────────────────
    const handleAddCard = (numColumn) => {
        setEditingCard(null)
        setTargetColumnId(numColumn)
        setCardModalOpen(true)
    }

    const handleEditCard = (card, numColumn) => {
        setEditingCard(card)
        setTargetColumnId(numColumn)
        setCardModalOpen(true)
    }

    const handleDeleteCard = async (numCard, numColumn) => {
        const card = getCards(numColumn).find(c => c.numCard === numCard)
        if (!card) return

        // Otimista: remove da tela imediatamente
        setLocalCards(prev => ({
            ...prev,
            [numColumn]: (prev[numColumn] ?? []).filter(c => c.numCard !== numCard)
        }))

        if (!USE_MOCK) {
            try {
                await cardService.deleteCard(card)
            } catch (error) {
                console.error('Erro ao deletar card:', error.message)
                // Reverte se a API falhar
                setLocalCards(prev => ({
                    ...prev,
                    [numColumn]: [...(prev[numColumn] ?? []), card]
                }))
            }
        }
    }

    const handleSaveCard = async (fields) => {
        if (USE_MOCK) {
            setLocalCards(prev => {
                const current = prev[targetColumnId] ?? []
                if (editingCard) {
                    return {
                        ...prev,
                        [targetColumnId]: current.map(c =>
                            c.numCard === editingCard.numCard ? { ...c, ...fields } : c
                        )
                    }
                }
                return {
                    ...prev,
                    [targetColumnId]: [...current, { numCard: Date.now(), ...fields }]
                }
            })
            return
        }

        try {
            if (editingCard) {
                // ── EDIÇÃO ────────────────────────────────────────────────────
                const updatedCard = await cardService.updateCard(
                    editingCard.numCard,
                    targetColumnId,
                    fields.title,
                    fields.description,
                    editingCard.ativo ?? true
                )

                // ── Verifica requisitionMessage ───────────────────────────────
                if (updatedCard?.requisitionMessage) {
                    setCardApiError(updatedCard.requisitionMessage)
                    return
                }
                // ─────────────────────────────────────────────────────────────

                setCardApiError(null)
                const localUpdated = { ...editingCard, ...fields }
                setLocalCards(prev => ({
                    ...prev,
                    [targetColumnId]: (prev[targetColumnId] ?? []).map(c =>
                        c.numCard === editingCard.numCard ? localUpdated : c
                    )
                }))
                setCardModalOpen(false)

            } else {
                // ── CRIAÇÃO ───────────────────────────────────────────────────
                const newCard = await cardService.createCard(
                    targetColumnId,
                    fields.title,
                    fields.description
                )
                setLocalCards(prev => ({
                    ...prev,
                    [targetColumnId]: [...(prev[targetColumnId] ?? []), newCard]
                }))
                setCardModalOpen(false)
            }
        } catch (error) {
            console.error(error.message)
        }
    }

    // ── Drag handlers ────────────────────────────────────────────────────────
    const handleDragStart = (numCard, fromColumnId) => {
        dragRef.current = { cardId: numCard, fromColumnId }
    }

    const handleDrop = async (toColumnId) => {
        const { cardId, fromColumnId } = dragRef.current

        if (!cardId || fromColumnId === toColumnId) return
        if (getCards(toColumnId).length >= 3) return

        const card = getCards(fromColumnId).find(c => c.numCard === cardId)
        if (!card) return

        setLocalCards(prev => {
            const fromCards = prev[fromColumnId] ?? []
            return {
                ...prev,
                [fromColumnId]: fromCards.filter(c => c.numCard !== cardId),
                [toColumnId]: [...(prev[toColumnId] ?? []), card]
            }
        })

        dragRef.current = { cardId: null, fromColumnId: null }

        if (!USE_MOCK) {
            try {
                await cardService.updateCard(
                    card.numCard,
                    toColumnId,
                    card.title,
                    card.description,
                    card.ativo ?? true
                )
            } catch (error) {
                console.error('Erro ao mover card:', error.message)
                setLocalCards(prev => ({
                    ...prev,
                    [fromColumnId]: [...(prev[fromColumnId] ?? []), card],
                    [toColumnId]: (prev[toColumnId] ?? []).filter(c => c.numCard !== cardId)
                }))
            }
        }
    }

    // ── Column handlers ──────────────────────────────────────────────────────
    const handleOpenAddColumn = () => {
        setEditingColumn(null)
        setColumnModalOpen(true)
    }

    const handleRenameColumn = (numColumn, currentName) => {
        setEditingColumn({ numColumn, name: currentName })
        setColumnModalOpen(true)
    }

    const handleDeleteColumn = async (numColumn) => {
        const column = columns.find(c => c.numColumn === numColumn)
        if (!column) return

        if (!USE_MOCK) {
            try {
                await columnService.deleteColumn(
                    column.numColumn,
                    boardId,
                    column.name,
                    column.order
                )
            } catch (error) {
                console.error('Erro ao excluir coluna:', error.message)
                return
            }
        }

        // Remove os cards da coluna do estado local
        setLocalCards(prev => {
            const updated = { ...prev }
            delete updated[numColumn]
            return updated
        })

        // Recarrega as colunas do backend (a coluna inativa não virá mais)
        loadColumns(boardId)
    }

    const handleSaveColumn = async (name) => {
        if (USE_MOCK) {
            setEditingColumn(null)
            setColumnModalOpen(false)
            return
        }

        try {
            if (editingColumn) {
                // ── RENOMEAR ──────────────────────────────────────────────────
                await columnService.updateColumn(
                    editingColumn.numColumn,
                    boardId,
                    name,
                    true,
                    columns.find(c => c.numColumn === editingColumn.numColumn)?.order ?? 0
                )
            } else {
                // ── CRIAR ─────────────────────────────────────────────────────
                await columnService.createColumn(name, boardId)
            }

            setEditingColumn(null)
            setColumnModalOpen(false)
            loadColumns(boardId)
        } catch (error) {
            console.error('Erro ao salvar coluna:', error.message)
        }
    }

    return (
        <div className='ColumnsAndCards'>
            <div className='ColumnList'>
                {columns.map(column => (
                    <li key={column.numColumn}>
                        <Column
                            columnId={column.numColumn}
                            columnName={column.name}
                            cards={getCards(column.numColumn)}
                            onAddCard={() => handleAddCard(column.numColumn)}
                            onEditCard={(card) => handleEditCard(card, column.numColumn)}
                            onDeleteCard={(numCard) => handleDeleteCard(numCard, column.numColumn)}
                            onRenameColumn={() => handleRenameColumn(column.numColumn, column.name)}
                            onDeleteColumn={() => handleDeleteColumn(column.numColumn)}
                            onDragStart={handleDragStart}
                            onDrop={handleDrop}
                        />
                    </li>
                ))}

                <li>
                    <button
                        className='AddColumn-btn'
                        onClick={handleOpenAddColumn}
                        disabled={columns.length >= 8}
                    >
                        <span>+</span>
                        Nova Coluna
                    </button>
                </li>
            </div>

            <CardModal
                isOpen={cardModalOpen}
                onClose={() => { setCardModalOpen(false); setCardApiError(null) }}
                onSave={handleSaveCard}
                card={editingCard}
                apiError={cardApiError}
            />
            <ColumnModal
                isOpen={columnModalOpen}
                onClose={() => { setColumnModalOpen(false); setEditingColumn(null) }}
                onSave={handleSaveColumn}
                column={editingColumn}
            />
        </div>
    )
}

export default ColumnsAndCards